import React, { useEffect, useState, useRef, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Save, MessageSquare, Mail, Sparkles, Image as ImageIcon,
  Upload, Trash2, Zap, RefreshCw, Eye, Bot, BarChart3, Plus, SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { SmbOutreachAutomationBanner } from '../../components/smb/SmbOutreachAutomationBanner';
import { AnalyticsTab } from '../../components/smb/OutreachAnalyticsTab';
import OutreachTuningTab from '../../components/smb/OutreachTuningTab';

const VARS_HINT = '{business_name}  •  {contact_name}  •  {tracking_url}';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImg = (u) => (!u ? null : (/^https?:\/\//i.test(u) ? u : `${BACKEND_URL}${u}`));

// ------------------------------------------------------------------
// Tab 1 — System Triggers (Meta-approved templates auto-fired)
// ------------------------------------------------------------------
const SystemTriggersTab = () => {
  const [config, setConfig] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async (force = false) => {
    setLoadingTpls(true);
    try {
      const r = await api.get(`/smb/outreach/automation/wa-templates${force ? '?force=true' : ''}`);
      if (r.data.success) setWaTemplates(r.data.data || []);
      if (force) toast.success(`Reloaded ${r.data.data?.length || 0} templates from Meta`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch WhatsApp templates from Meta');
    } finally {
      setLoadingTpls(false);
    }
  };

  const fetchConfig = async () => {
    setLoadingCfg(true);
    try {
      const r = await api.get('/smb/outreach/automation/config');
      if (r.data.success) setConfig(r.data.data);
    } catch {
      toast.error('Failed to load automation config');
    } finally {
      setLoadingCfg(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const r = await api.get('/smb/statuses');
      if (r.data.success) setStatuses(r.data.data || []);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => { fetchConfig(); fetchTemplates(false); fetchStatuses(); }, []);

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const r = await api.put('/smb/outreach/automation/config', {
        trigger: config.trigger,
        followups: config.followups,
      });
      if (r.data.success) {
        setConfig(r.data.data);
        toast.success('Automation saved');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setTrigger = (field, value) => setConfig(c => ({ ...c, trigger: { ...c.trigger, [field]: value } }));
  const setFollowup = (idx, field, value) => setConfig(c => ({
    ...c,
    followups: c.followups.map((f, i) => i === idx ? { ...f, [field]: value } : f),
  }));

  const templatesByName = useMemo(() => {
    const m = {};
    waTemplates.forEach(t => { m[t.name] = t; });
    return m;
  }, [waTemplates]);

  const renderTemplatePicker = (selectedName, selectedLang, onChange) => {
    const onPick = (name) => {
      const tpl = templatesByName[name];
      onChange({ template_name: name, template_lang: tpl?.language || 'en' });
    };
    return (
      <Select value={selectedName || '__none__'} onValueChange={(v) => onPick(v === '__none__' ? '' : v)}>
        <SelectTrigger className="w-full" data-testid="trigger-template-select">
          <SelectValue placeholder="Pick an approved template..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None (disabled) —</SelectItem>
          {waTemplates.map(t => (
            <SelectItem key={`${t.name}_${t.language}`} value={t.name}>
              <div className="flex flex-col">
                <span className="font-medium">{t.name}</span>
                <span className="text-[10px] opacity-60 uppercase">{t.language} · {t.category}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderPreview = (templateName) => {
    if (!templateName) return null;
    const tpl = templatesByName[templateName];
    if (!tpl?.body_preview) return null;
    // Sample data used for the preview substitution
    const sample = {
      business_name: 'Acme Pizza',
      contact_name: 'Rohan',
      tracking_url: 'https://myzow.com/p/abc123',
    };
    const aliases = {
      businessname: 'business_name', company: 'business_name', company_name: 'business_name',
      shop_name: 'business_name', store_name: 'business_name',
      name: 'contact_name', contactname: 'contact_name', first_name: 'contact_name',
      firstname: 'contact_name', customer_name: 'contact_name',
      trackingurl: 'tracking_url', preview_url: 'tracking_url', previewurl: 'tracking_url',
      url: 'tracking_url', link: 'tracking_url', site_link: 'tracking_url',
    };
    const positional = ['business_name', 'contact_name', 'tracking_url'];
    const rendered = tpl.body_preview.replace(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*|\d+)\s*\}\}/g, (_m, token) => {
      if (/^\d+$/.test(token)) {
        const key = positional[parseInt(token, 10) - 1];
        return key ? sample[key] : `{{${token}}}`;
      }
      const key = aliases[token.toLowerCase()] || token;
      return sample[key] !== undefined ? sample[key] : `{{${token}}}`;
    });
    const vars = tpl.variables || [];
    const kind = tpl.variables_kind || 'none';
    return (
      <div className="mt-2 space-y-2" data-testid="preview-rendered">
        {kind !== 'none' && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] uppercase font-semibold text-muted-foreground">Variables:</span>
            <Badge variant="outline" className="text-[10px] py-0 h-5">
              {kind === 'named' ? 'named' : 'positional'}
            </Badge>
            {vars.map(v => (
              <Badge key={v} className="text-[10px] py-0 h-5 bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 font-mono">
                {`{{${v}}}`}
              </Badge>
            ))}
          </div>
        )}
        <div className="p-2 rounded-md bg-muted/30 border border-border">
          <p className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1 mb-1"><Eye className="h-3 w-3" />Preview (with sample data)</p>
          <p className="text-xs whitespace-pre-wrap font-mono">{rendered}</p>
        </div>
      </div>
    );
  };

  if (loadingCfg || !config) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="system-triggers-tab">
      <SmbOutreachAutomationBanner />
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="p-3 text-sm flex items-start gap-3">
          <Zap className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">How this works</p>
            <p className="text-xs text-muted-foreground mt-1">
              When a lead's status changes to the configured value below, the <strong>initial trigger</strong> fires immediately.
              If the lead doesn't reply, the <strong>follow-ups</strong> fire automatically at the configured delays.
              Templates are fetched live from Meta — only <code className="bg-background border rounded px-1 py-0.5 text-[10px]">APPROVED</code> templates appear in the dropdowns.
              The trigger is idempotent — re-applying the same status to a lead with an active outreach job won't re-send.
            </p>
            <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5">
              <strong>Marketing window:</strong> dispatches only between <strong>09:00 – 21:00 IST</strong> (WhatsApp + Email). Anything triggered outside the window is auto-deferred to the next 09:00 IST.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => fetchTemplates(true)} disabled={loadingTpls} data-testid="refresh-meta-btn">
            {loadingTpls ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh from Meta
          </Button>
        </CardContent>
      </Card>

      {/* Initial trigger */}
      <Card data-testid="trigger-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 uppercase">Initial</Badge>
              <span>Fires when a lead's status changes to:</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Active</Label>
              <Switch checked={!!config.trigger.active} onCheckedChange={v => setTrigger('active', v)} data-testid="trigger-active" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Trigger status</Label>
            <Select value={config.trigger.status || ''} onValueChange={(v) => setTrigger('status', v)}>
              <SelectTrigger className="w-full" data-testid="trigger-status-select">
                <SelectValue placeholder="Pick a lead status..." />
              </SelectTrigger>
              <SelectContent>
                {statuses.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#888' }} />
                      {s.label}
                      <span className="text-[10px] opacity-60 font-mono">({s.value})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Meta-approved template</Label>
            {renderTemplatePicker(config.trigger.template_name, config.trigger.template_lang, ({ template_name, template_lang }) => {
              setTrigger('template_name', template_name);
              setTrigger('template_lang', template_lang);
            })}
            {renderPreview(config.trigger.template_name)}
          </div>
        </CardContent>
      </Card>

      {/* Follow-ups */}
      <Card data-testid="followups-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Badge className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/30 uppercase">Follow-ups</Badge>
            <span className="text-xs text-muted-foreground font-normal">Fired only if the lead hasn't replied</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.followups.map((f, idx) => (
            <div key={idx} className="rounded-md border border-border p-3 space-y-2" data-testid={`followup-${idx}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="uppercase">#{idx + 1}</Badge>
                  <Label className="text-xs">Delay (hours)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={f.delay_hours}
                    onChange={e => setFollowup(idx, 'delay_hours', parseInt(e.target.value || '0', 10))}
                    className="w-24 h-8"
                    data-testid={`followup-delay-${idx}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Active</Label>
                  <Switch checked={!!f.active} onCheckedChange={v => setFollowup(idx, 'active', v)} data-testid={`followup-active-${idx}`} />
                </div>
              </div>
              {renderTemplatePicker(f.template_name, f.template_lang, ({ template_name, template_lang }) => {
                setFollowup(idx, 'template_name', template_name);
                setFollowup(idx, 'template_lang', template_lang);
              })}
              {renderPreview(f.template_name)}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} data-testid="trigger-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Automation
        </Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Tab 2 — Inbox Gallery (per-template image + caption for live chat)
// Up to 20 user-named items, fully independent of the trigger sequence.
// ------------------------------------------------------------------
const GALLERY_MAX = 20;

const InboxGalleryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // id → {name, caption}
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/smb/outreach/gallery');
      if (r.data.success) {
        setItems(r.data.data || []);
        const d = {};
        (r.data.data || []).forEach(it => { d[it.id] = { name: it.name || '', caption: it.caption || '' }; });
        setDrafts(d);
      }
    } catch {
      toast.error('Failed to load gallery');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (id, field, value) => setDrafts(p => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));

  const addItem = async () => {
    if (items.length >= GALLERY_MAX) {
      toast.error(`Maximum ${GALLERY_MAX} templates reached`);
      return;
    }
    // Start with a placeholder name so the backend (which enforces non-empty)
    // accepts the create. The user is expected to rename it immediately —
    // the input is highlighted as a hint.
    try {
      const r = await api.post('/smb/outreach/gallery', { name: 'Untitled template', caption: '' });
      if (r.data.success) {
        toast.success('Template added');
        await load(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add template');
    }
  };

  const saveItem = async (id) => {
    const d = drafts[id] || {};
    const name = (d.name || '').trim();
    if (!name) {
      toast.error('Template name is required');
      return;
    }
    setSavingId(id);
    try {
      const r = await api.put(`/smb/outreach/gallery/${id}`, { name, caption: d.caption || '' });
      if (r.data.success) {
        toast.success('Saved');
        load(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG / PNG / WebP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/smb/outreach/gallery/${id}/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        toast.success('Image uploaded');
        load(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const removeImage = async (id) => {
    if (!window.confirm('Remove the template image?')) return;
    setUploadingId(id);
    try {
      await api.delete(`/smb/outreach/gallery/${id}/image`);
      toast.success('Image removed');
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setUploadingId(null);
    }
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete template "${name || 'Untitled'}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/smb/outreach/gallery/${id}`);
      toast.success('Template deleted');
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="inbox-gallery-tab">
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="p-3 text-sm flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <p className="font-medium">Per-template image + caption for the SMB Inbox composer.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Agents see these in the "Templates" picker inside live chats — one click drops the image + caption into the composer. Up to {GALLERY_MAX} templates.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{items.length} / {GALLERY_MAX}</span>
            <Button onClick={addItem} disabled={items.length >= GALLERY_MAX} size="sm" data-testid="gallery-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" />Add Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="gallery-empty-state">
            No templates yet. Click <strong>Add Template</strong> above to create one.
          </CardContent>
        </Card>
      ) : items.map(t => {
        const d = drafts[t.id] || { name: t.name || '', caption: t.caption || '' };
        return (
          <Card key={t.id} data-testid={`gallery-row-${t.id}`}>
            <CardContent className="p-4 space-y-3">
              {/* Header row — name input + save/delete */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Label htmlFor={`gallery-name-${t.id}`} className="text-xs text-muted-foreground">Template Name *</Label>
                  <Input
                    id={`gallery-name-${t.id}`}
                    value={d.name}
                    onChange={e => setField(t.id, 'name', e.target.value)}
                    placeholder="e.g. Pricing intro, Demo invite, Follow-up #2"
                    maxLength={80}
                    data-testid={`gallery-name-${t.id}`}
                    required
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={() => saveItem(t.id)} size="sm" disabled={savingId === t.id} data-testid={`gallery-save-${t.id}`}>
                    {savingId === t.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                  <Button onClick={() => deleteItem(t.id, t.name)} size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-500/10" data-testid={`gallery-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Image + caption row */}
              <div className="flex items-start gap-3">
                {t.image_url ? (
                  <img src={resolveImg(t.image_url)} alt={t.name || 'gallery'} className="h-24 w-24 rounded-md object-cover bg-muted border border-border" data-testid={`gallery-thumb-${t.id}`} />
                ) : (
                  <div className="h-24 w-24 rounded-md bg-muted/40 border border-border flex items-center justify-center text-muted-foreground" data-testid={`gallery-empty-${t.id}`}>
                    <ImageIcon className="h-6 w-6 opacity-50" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={el => { fileInputs.current[t.id] = el; }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={e => { uploadImage(t.id, e.target.files?.[0]); e.target.value = ''; }}
                      data-testid={`gallery-input-${t.id}`}
                    />
                    <Button type="button" size="sm" variant="outline" onClick={() => fileInputs.current[t.id]?.click()} disabled={uploadingId === t.id} data-testid={`gallery-upload-${t.id}`}>
                      {uploadingId === t.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                      {t.image_url ? 'Replace' : 'Upload'}
                    </Button>
                    {t.image_url && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeImage(t.id)} disabled={uploadingId === t.id} className="text-red-500 hover:text-red-600 hover:bg-red-500/10" data-testid={`gallery-remove-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove Image
                      </Button>
                    )}
                  </div>
                  <Textarea
                    rows={3}
                    value={d.caption}
                    onChange={e => setField(t.id, 'caption', e.target.value)}
                    placeholder="Caption that gets pre-filled in the composer when this template is picked..."
                    className="text-xs"
                    data-testid={`gallery-caption-${t.id}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------------
// Tab 3 — Email Templates (Subject + HTML body editor)
// ------------------------------------------------------------------
const EmailTemplatesTab = ({ templates, drafts, set, save, saving }) => (
  <div className="space-y-4" data-testid="email-templates-tab">
    <Card className="bg-blue-500/5 border-blue-500/20">
      <CardContent className="p-3 text-sm">
        <p className="font-medium">Available variables in email body/subject:</p>
        <code className="text-xs bg-background border rounded px-2 py-1 inline-block mt-1">{VARS_HINT}</code>
        <p className="text-xs text-muted-foreground mt-2">
          <strong>tracking_url</strong> is auto-generated per lead and per message — it logs the click then redirects to the lead's preview URL.
        </p>
      </CardContent>
    </Card>

    {templates.map(t => {
      const d = drafts[t.key] || t;
      return (
        <Card key={t.key} data-testid={`email-row-${t.key}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Badge className="bg-primary/10 text-primary uppercase">{t.key.replace('_', ' ')}</Badge>
                {t.key === 'msg_1' && <span className="text-xs text-muted-foreground font-normal">Sent immediately when preview is set</span>}
                {t.key === 'msg_2' && <span className="text-xs text-muted-foreground font-normal">Sent +24 hours after Message 1</span>}
                {t.key === 'msg_3' && <span className="text-xs text-muted-foreground font-normal">Sent +72 hours after Message 1</span>}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Active</Label>
                <Switch checked={!!d.active} onCheckedChange={v => set(t.key, 'active', v)} data-testid={`email-active-${t.key}`} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Mail className="h-3.5 w-3.5" />Email Subject</Label>
              <Input value={d.email_subject || ''} onChange={e => set(t.key, 'email_subject', e.target.value)} data-testid={`email-subject-${t.key}`} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email Body (HTML)</Label>
              <Textarea
                rows={8}
                value={d.email_body_html || ''}
                onChange={e => set(t.key, 'email_body_html', e.target.value)}
                className="font-mono text-xs"
                data-testid={`email-body-${t.key}`}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => save(t.key)} disabled={saving === t.key} data-testid={`email-save-${t.key}`}>
                {saving === t.key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// ------------------------------------------------------------------
// Page root
// ------------------------------------------------------------------
const SMBOutreachTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  // Controlled tab state so it survives the brief `loading=true` flicker
  // that happens after upload/save → load() refresh.
  const [activeTab, setActiveTab] = useState('triggers');
  const fileInputs = useRef({});

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/smb/outreach/templates');
      if (r.data.success) {
        setTemplates(r.data.data);
        const d = {};
        r.data.data.forEach(t => { d[t.key] = { ...t }; });
        setDrafts(d);
      }
    } catch {
      toast.error('Failed to load templates');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, field, value) => setDrafts(p => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } }));

  const save = async (key) => {
    setSaving(key);
    try {
      const d = drafts[key];
      const payload = {
        whatsapp_template_name: d.whatsapp_template_name,
        whatsapp_template_lang: d.whatsapp_template_lang,
        email_subject: d.email_subject,
        email_body_html: d.email_body_html,
        active: d.active,
        gallery_caption: d.gallery_caption || '',
      };
      const r = await api.put(`/smb/outreach/templates/${key}`, payload);
      if (r.data.success) {
        toast.success(`${key} saved`);
        load(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const uploadImage = async (key, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG / PNG / WebP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/smb/outreach/templates/${key}/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        toast.success('Image uploaded');
        load(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const removeImage = async (key) => {
    if (!window.confirm('Remove the template image?')) return;
    setUploadingKey(key);
    try {
      await api.delete(`/smb/outreach/templates/${key}/image`);
      toast.success('Image removed');
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setUploadingKey(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-outreach-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-primary" />Outreach Automation</h1>
        <p className="text-muted-foreground text-sm">Pre-approved WhatsApp triggers, Inbox gallery, and email cadence — all in one place.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="outreach-tabs">
          <TabsTrigger value="triggers" data-testid="tab-triggers"><Bot className="h-3.5 w-3.5 mr-1.5" />System Triggers</TabsTrigger>
          <TabsTrigger value="tuning" data-testid="tab-tuning"><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Tuning</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery"><ImageIcon className="h-3.5 w-3.5 mr-1.5" />Inbox Gallery</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email Templates</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-3.5 w-3.5 mr-1.5" />Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="triggers" className="mt-4">
          <SystemTriggersTab />
        </TabsContent>

        <TabsContent value="tuning" className="mt-4">
          <OutreachTuningTab />
        </TabsContent>

        <TabsContent value="gallery" className="mt-4">
          <InboxGalleryTab />
        </TabsContent>

        <TabsContent value="email" className="mt-4">
          <EmailTemplatesTab templates={templates} drafts={drafts} set={set} save={save} saving={saving} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SMBOutreachTemplates;
